import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  createFlowMutation,
  createFlowTriggerMutation,
  type FlowCreate,
  type FlowStepConfig,
  type FlowStepResponse,
  type FlowTriggerCreate,
  type FlowUpdate,
  getFlow,
  getFlowQueryKey,
  listFlowsQueryKey,
  listFlowTriggersQueryKey,
  updateFlowMutation,
} from "@/api";
import type { FlowFormState } from "@/features/build/workflows/FlowForm";
import type { DraftView } from "../draftModel";
import { useDraftActions } from "../useDraftActions";
import { useDraftRun } from "../useDraftRun";
import type { DraftStepDef } from "../useMultiStepCreate";

/** The workflow draft as propose_draft validated it (WorkflowDraft). */
export interface WorkflowDraftBody {
  flow?: Partial<FlowCreate>;
  triggers?: FlowTriggerCreate[];
}

/**
 * Each step keeps the stored settings of the step it takes over, the n-th of its kind: a draft says only what a
 * step does (its prompt), so a webhook's address and headers or an agent step's limits survive a Save that
 * replaces the steps, and nothing in a draft can point stored credentials somewhere else.
 */
function keepStoredSettings(steps: readonly FlowStepConfig[], stored: readonly FlowStepResponse[]): FlowStepConfig[] {
  const byKind = new Map<string, FlowStepResponse[]>();
  for (const step of [...stored].sort((a, b) => a.stepOrder - b.stepOrder)) {
    const kind = step.stepType ?? "agent";
    byKind.set(kind, [...(byKind.get(kind) ?? []), step]);
  }
  const seen = new Map<string, number>();
  return steps.map((step) => {
    const kind = step.stepType ?? "agent";
    const nth = seen.get(kind) ?? 0;
    seen.set(kind, nth + 1);
    const old = byKind.get(kind)?.[nth];
    return old ? { ...step, config: { ...old.config, prompt: step.config?.prompt ?? old.config?.prompt } } : step;
  });
}

/**
 * Create (the flow with its steps and connectors, then its triggers) or Save (PUT; steps and connectors are
 * replaced together, triggers stay on the Triggers tab) for a workflow draft card.
 */
export function useWorkflowDraft(draft: DraftView) {
  const { t } = useTranslation("buildChat", { keyPrefix: "workflow" });
  const navigate = useNavigate();
  const actions = useDraftActions();
  const create = useMutation(createFlowMutation());
  const update = useMutation(updateFlowMutation());
  const addTrigger = useMutation(createFlowTriggerMutation());
  const flowUuid = useRef<string | null>(draft.targetUuid);
  // Triggers already added in this card, so a retry doesn't add them twice.
  const added = useRef(new Set<number>());

  const body = draft.draft as WorkflowDraftBody;
  const { teamUuid: _team, ...flow } = body.flow ?? {};
  const triggers = body.triggers ?? [];

  const steps: DraftStepDef[] = draft.targetUuid
    ? [
        {
          key: "save",
          label: t("steps.save"),
          run: async () => {
            const path = { flow_uuid: draft.targetUuid ?? "" };
            const { steps: flowSteps, connectors, ...details } = flow;
            const stored = flowSteps ? ((await getFlow({ path, throwOnError: true })).data.data.steps ?? []) : [];
            const change: FlowUpdate = {
              ...details,
              // The server replaces connectors only alongside steps; never send one without the other.
              ...(flowSteps ? { steps: keepStoredSettings(flowSteps, stored), connectors: connectors ?? [] } : {}),
            };
            await update.mutateAsync({ path, body: change, throwOnError: true });
          },
        },
      ]
    : [
        {
          key: "create",
          label: t("steps.create"),
          run: async () => {
            const result = await create.mutateAsync({
              // The form's rule: no team; the creator gets access and shares it from the Access tab.
              body: { ...(flow as FlowCreate), sourceSessionUuid: actions.sessionUuid },
              throwOnError: true,
            });
            flowUuid.current = result.data.uuid;
          },
        },
        {
          key: "triggers",
          label: t("steps.triggers"),
          skip: triggers.length === 0,
          run: async () => {
            for (const [index, trigger] of triggers.entries()) {
              if (added.current.has(index)) continue;
              const path = { flow_uuid: flowUuid.current ?? "" };
              // Sequential by design: triggers are added in the draft's order, and a retry skips the ones added.
              await addTrigger.mutateAsync({ path, body: trigger, throwOnError: true }); // NOSONAR
              added.current.add(index);
            }
          },
        },
      ];
  const run = useDraftRun(draft.draftId, steps, {
    fallback: t("failed"),
    success: draft.targetUuid ? t("saved") : t("created"),
    entityUuid: () => flowUuid.current,
    invalidate: (uuid) => [
      listFlowsQueryKey(),
      getFlowQueryKey({ path: { flow_uuid: uuid } }),
      listFlowTriggersQueryKey({ path: { flow_uuid: uuid } }),
    ],
  });

  return {
    flow,
    triggers,
    ...run,
    openInForm: draft.targetUuid
      ? undefined
      : () =>
          navigate("/build/workflows/new", {
            state: {
              draft: {
                name: flow.name,
                description: flow.description ?? undefined,
                purpose: flow.purpose ?? undefined,
                steps: flow.steps ?? [],
                connectors: flow.connectors ?? [],
              },
              sourceSessionUuid: actions.sessionUuid,
            } satisfies FlowFormState,
          }),
  };
}
